import { api } from "./index"
import {
    CreateProjectPayload,
    PaginatedResponse,
    ProjectFunding,
    ProjectPaymentsFilters,
    ProjectPhoto,
    ProjectStatsResponse,
    ProjectSubscription,
    ProjectSubscriptionFilters,
    ProjectSubscriptionPayment,
    UpdateProjectPayload,
} from "../types/projects"

const buildProjectFormData = (
    payload: Partial<CreateProjectPayload | UpdateProjectPayload>,
): FormData => {
    const formData = new FormData()

    if (payload.translations !== undefined) {
        formData.append("translations", JSON.stringify(payload.translations))
    }
    if (payload.target_amount !== undefined) {
        formData.append("target_amount", payload.target_amount)
    }
    if (payload.min_subscription_amount !== undefined) {
        formData.append("min_subscription_amount", payload.min_subscription_amount)
    }
    if (payload.max_coefficient !== undefined) {
        formData.append("max_coefficient", String(payload.max_coefficient))
    }
    if (payload.dividend_pool_percent !== undefined) {
        formData.append("dividend_pool_percent", String(payload.dividend_pool_percent))
    }
    if (payload.sort_order !== undefined) {
        formData.append("sort_order", String(payload.sort_order))
    }
    if (payload.is_subscription_active !== undefined) {
        formData.append(
            "is_subscription_active",
            payload.is_subscription_active ? "1" : "0",
        )
    }
    if (payload.logo instanceof File) {
        formData.append("logo", payload.logo)
    }

    return formData
}

const adaptMutationHook = (
    hook: () => readonly [(...args: any[]) => any, any],
) => () => {
    const [trigger, state] = hook()
    return {
        ...state,
        mutate: trigger,
        mutateAsync: async (...args: any[]) => {
            const result = trigger(...args)
            return result.unwrap ? result.unwrap() : result
        },
    }
}

export interface ProjectsQueryParams {
    page?: number
    per_page?: number
    sort?: string
    direction?: "asc" | "desc"
}

const projectsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getAdminProjects: builder.query<
            PaginatedResponse<ProjectFunding>,
            ProjectsQueryParams | undefined
        >({
            query: (params: ProjectsQueryParams = {}) => ({
                url: "/admin/projects",
                params: params ?? undefined,
            }),
            providesTags: (result) => [
                { type: "Projects", id: "LIST" },
                ...(result?.data?.map((project) => ({
                    type: "Projects" as const,
                    id: project.id,
                })) ?? []),
            ],
        }),
        getAdminProject: builder.query<{ data: ProjectFunding }, number>({
            query: (projectId) => `/admin/projects/${projectId}`,
            providesTags: (_result, _error, projectId) => [
                { type: "Projects", id: projectId },
            ],
        }),
        getAdminProjectStats: builder.query<ProjectStatsResponse, number>({
            query: (projectId) => `/admin/projects/${projectId}/stats`,
            providesTags: (_result, _error, projectId) => [
                { type: "ProjectStats", id: projectId },
            ],
        }),
        getAdminProjectGallery: builder.query<
            { data: ProjectPhoto[] },
            number
        >({
            query: (projectId) => `/admin/projects/${projectId}/gallery`,
            providesTags: (_result, _error, projectId) => [
                { type: "ProjectGallery", id: projectId },
            ],
        }),
        createAdminProject: builder.mutation<any, CreateProjectPayload>({
            query: (payload) => ({
                url: "/admin/projects",
                method: "POST",
                body: buildProjectFormData(payload),
            }),
            invalidatesTags: [{ type: "Projects", id: "LIST" }],
        }),
        updateAdminProject: builder.mutation<any, UpdateProjectPayload>({
            query: ({ id, ...rest }) => {
                const formData = buildProjectFormData(rest)
                formData.append("_method", "PATCH")
                return {
                    url: `/admin/projects/${id}`,
                    method: "POST",
                    body: formData,
                }
            },
            invalidatesTags: (_result, _error, { id }) => [
                { type: "Projects", id },
                { type: "Projects", id: "LIST" },
                { type: "ProjectStats", id },
            ],
        }),
        deleteAdminProject: builder.mutation<any, number>({
            query: (projectId) => ({
                url: `/admin/projects/${projectId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, projectId) => [
                { type: "Projects", id: "LIST" },
                { type: "Projects", id: projectId },
            ],
        }),
        uploadAdminProjectPhoto: builder.mutation<
            any,
            {
                projectId: number
                file: File
                isActive?: boolean
                sortOrder?: number
            }
        >({
            query: ({ projectId, file, isActive, sortOrder }) => {
                const formData = new FormData()
                formData.append("photo", file)
                if (isActive !== undefined) {
                    formData.append("is_active", isActive ? "1" : "0")
                }
                if (sortOrder !== undefined) {
                    formData.append("sort_order", String(sortOrder))
                }
                return {
                    url: `/admin/projects/${projectId}/gallery`,
                    method: "POST",
                    body: formData,
                }
            },
            invalidatesTags: (_result, _error, { projectId }) => [
                { type: "ProjectGallery", id: projectId },
            ],
        }),
        updateAdminProjectPhoto: builder.mutation<
            any,
            {
                projectId: number
                photoId: number
                file?: File
                isActive?: boolean
                sortOrder?: number
            }
        >({
            query: ({ projectId, photoId, file, isActive, sortOrder }) => {
                const formData = new FormData()
                formData.append("_method", "PATCH")
                if (file) {
                    formData.append("photo", file)
                }
                if (isActive !== undefined) {
                    formData.append("is_active", isActive ? "1" : "0")
                }
                if (sortOrder !== undefined) {
                    formData.append("sort_order", String(sortOrder))
                }
                return {
                    url: `/admin/projects/${projectId}/gallery/${photoId}`,
                    method: "POST",
                    body: formData,
                }
            },
            invalidatesTags: (_result, _error, { projectId }) => [
                { type: "ProjectGallery", id: projectId },
            ],
        }),
        deleteAdminProjectPhoto: builder.mutation<
            any,
            { projectId: number; photoId: number }
        >({
            query: ({ projectId, photoId }) => ({
                url: `/admin/projects/${projectId}/gallery/${photoId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, { projectId }) => [
                { type: "ProjectGallery", id: projectId },
            ],
        }),
        getAdminProjectSubscriptions: builder.query<
            PaginatedResponse<ProjectSubscription>,
            { projectId: number; filters?: ProjectSubscriptionFilters }
        >({
            query: ({ projectId, filters = {} }) => ({
                url: `/admin/projects/${projectId}/subscriptions`,
                params: filters,
            }),
            providesTags: (_result, _error, { projectId }) => [
                { type: "ProjectSubscriptions", id: projectId },
            ],
        }),
        updateAdminProjectSubscription: builder.mutation<
            any,
            {
                projectId: number
                subscriptionId: number
                payload: Partial<{
                    coefficient: number
                    billing_day: number
                    is_active: boolean
                    dividend_share_percent?: number
                    dividend_locked_at?: string | null
                }>
            }
        >({
            query: ({ projectId, subscriptionId, payload }) => ({
                url: `/admin/projects/${projectId}/subscriptions/${subscriptionId}`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: (_result, _error, { projectId }) => [
                { type: "ProjectSubscriptions", id: projectId },
                { type: "ProjectStats", id: projectId },
            ],
        }),
        getAdminProjectPayments: builder.query<
            PaginatedResponse<ProjectSubscriptionPayment>,
            { projectId: number; filters?: ProjectPaymentsFilters }
        >({
            query: ({ projectId, filters = {} }) => ({
                url: `/admin/projects/${projectId}/payments`,
                params: filters,
            }),
            providesTags: (_result, _error, { projectId }) => [
                { type: "ProjectPayments", id: projectId },
            ],
        }),
        createAdminProjectPayment: builder.mutation<
            any,
            {
                projectId: number
                subscriptionId: number
                amount: string
                currency: "USD" | "USDT"
                coefficient?: number
                charged_at?: string
            }
        >({
            query: ({ projectId, subscriptionId, ...body }) => ({
                url: `/admin/projects/${projectId}/subscriptions/${subscriptionId}/payments`,
                method: "POST",
                body,
            }),
            invalidatesTags: (_result, _error, { projectId }) => [
                { type: "ProjectPayments", id: projectId },
                { type: "ProjectSubscriptions", id: projectId },
                { type: "ProjectStats", id: projectId },
            ],
        }),
    }),
    overrideExisting: false,
})

const {
    useGetAdminProjectsQuery,
    useGetAdminProjectQuery,
    useGetAdminProjectStatsQuery,
    useGetAdminProjectGalleryQuery,
    useCreateAdminProjectMutation,
    useUpdateAdminProjectMutation,
    useDeleteAdminProjectMutation,
    useUploadAdminProjectPhotoMutation,
    useUpdateAdminProjectPhotoMutation,
    useDeleteAdminProjectPhotoMutation,
    useGetAdminProjectSubscriptionsQuery,
    useUpdateAdminProjectSubscriptionMutation,
    useGetAdminProjectPaymentsQuery,
    useCreateAdminProjectPaymentMutation,
} = projectsApi

export const useProjects = (params: ProjectsQueryParams = {}) =>
    useGetAdminProjectsQuery(params)

export const useProject = (projectId: number | null) =>
    useGetAdminProjectQuery(projectId as number, {
        skip: projectId === null,
    })

export const useProjectDetail = (projectId: number | null) =>
    useProject(projectId)

export const useProjectStats = (projectId: number | null) =>
    useGetAdminProjectStatsQuery(projectId as number, {
        skip: projectId === null,
    })

export const useProjectGallery = (projectId: number | null) =>
    useGetAdminProjectGalleryQuery(projectId as number, {
        skip: projectId === null,
    })

export const useProjectSubscriptions = (
    projectId: number | null,
    filters: ProjectSubscriptionFilters = {},
) =>
    useGetAdminProjectSubscriptionsQuery(
        { projectId: projectId as number, filters },
        { skip: projectId === null },
    )

export const useProjectPayments = (
    projectId: number | null,
    filters: ProjectPaymentsFilters = {},
) =>
    useGetAdminProjectPaymentsQuery(
        { projectId: projectId as number, filters },
        { skip: projectId === null },
    )

export const useCreateProject = adaptMutationHook(
    useCreateAdminProjectMutation,
)
export const useUpdateProject = adaptMutationHook(
    useUpdateAdminProjectMutation,
)
export const useDeleteProject = adaptMutationHook(
    useDeleteAdminProjectMutation,
)
export const useUploadProjectPhoto = adaptMutationHook(
    useUploadAdminProjectPhotoMutation,
)
export const useUpdateProjectPhoto = adaptMutationHook(
    useUpdateAdminProjectPhotoMutation,
)
export const useDeleteProjectPhoto = adaptMutationHook(
    useDeleteAdminProjectPhotoMutation,
)
export const useUpdateProjectSubscription = adaptMutationHook(
    useUpdateAdminProjectSubscriptionMutation,
)
export const useCreateProjectPayment = adaptMutationHook(
    useCreateAdminProjectPaymentMutation,
)
